import { DollarSign, CheckCircle, Clock, AlertTriangle, Edit3, X } from "lucide-react";
import { useState } from "react";
import api from "../../services/api";

var STATUS_LABELS = { pendente: "Pendente", processando: "Processando", pago: "Pago", travada: "Travada" };
var STATUS_COLORS_MAP = {
  pendente: "bg-amber-100 text-amber-700",
  processando: "bg-blue-100 text-blue-700",
  pago: "bg-emerald-100 text-emerald-700",
  travada: "bg-red-100 text-red-700",
};
var PRODUCT_LABELS = { consorcio: "Consórcio", seguro: "Seguro", carta_contemplada: "Carta Contemplada" };
var PRODUCT_COLORS_MAP = { consorcio: "bg-[#F26A4B]/10 text-[#F26A4B]", seguro: "bg-[#3B82F6]/10 text-[#3B82F6]", carta_contemplada: "bg-emerald-100 text-emerald-700" };

function fmtVal(v) {
  if (!v) return "R$ 0";
  if (v >= 1000) return "R$ " + (v / 1000).toFixed(1) + "k";
  return "R$ " + v.toFixed(0);
}

function fmtFull(v) {
  if (v == null) return "R$ 0,00";
  return "R$ " + v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch(e) { return "-"; }
}

export function CommissionKPIs({ totalGeral, totalPago, totalPendente, totalTravada }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6">
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-emerald-500/10 rounded-xl"><DollarSign className="w-5 h-5 text-emerald-500" /></div>
          <span className="text-xs text-gray-500">Total Geral</span>
        </div>
        <p className="text-xl lg:text-2xl font-bold text-[#0A2F3D]" data-testid="kpi-total-geral">{fmtVal(totalGeral)}</p>
      </div>
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-emerald-500/10 rounded-xl"><CheckCircle className="w-5 h-5 text-emerald-500" /></div>
          <span className="text-xs text-gray-500">Pagas</span>
        </div>
        <p className="text-xl lg:text-2xl font-bold text-emerald-600" data-testid="kpi-total-pago">{fmtVal(totalPago)}</p>
      </div>
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-amber-500/10 rounded-xl"><Clock className="w-5 h-5 text-amber-500" /></div>
          <span className="text-xs text-gray-500">Pendentes</span>
        </div>
        <p className="text-xl lg:text-2xl font-bold text-amber-600" data-testid="kpi-total-pendente">{fmtVal(totalPendente)}</p>
      </div>
      <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-red-500/10 rounded-xl"><AlertTriangle className="w-5 h-5 text-red-500" /></div>
          <span className="text-xs text-gray-500">Travadas</span>
        </div>
        <p className="text-xl lg:text-2xl font-bold text-red-600" data-testid="kpi-total-travada">{fmtVal(totalTravada)}</p>
      </div>
    </div>
  );
}

export function CommissionRow({ c, isAdmin, onEdit }) {
  var pCls = PRODUCT_COLORS_MAP[c.product_type] || "bg-gray-100 text-gray-700";
  var pLabel = PRODUCT_LABELS[c.product_type] || c.product_type;
  var sCls = STATUS_COLORS_MAP[c.status] || "bg-gray-100 text-gray-700";
  var sLabel = STATUS_LABELS[c.status] || c.status;
  return (
    <tr className="border-b border-gray-50 hover:bg-[#3B82F6]/[0.02]" data-testid={"commission-row-" + c.id}>
      <td className="py-3 px-4 text-sm font-medium text-[#0A2F3D]">{c.lead_name || "-"}</td>
      <td className="py-3 px-4"><span className={"px-2 py-1 text-xs font-medium rounded-full " + pCls}>{pLabel}</span></td>
      <td className="py-3 px-4 text-xs text-gray-500 capitalize">{c.commission_type}</td>
      <td className="py-3 px-4 text-sm font-semibold text-emerald-600">{fmtFull(c.value)}</td>
      <td className="py-3 px-4"><span className={"px-2.5 py-1 text-xs font-medium rounded-full " + sCls}>{sLabel}</span></td>
      {isAdmin && <td className="py-3 px-4 text-xs text-gray-500">{c.broker_name || "-"}</td>}
      <td className="py-3 px-4 text-xs text-gray-400">{fmtDate(c.created_at)}</td>
      {isAdmin && (
        <td className="py-3 px-4 text-right">
          <button onClick={function() { onEdit(c); }} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-[#3B82F6]" data-testid={"commission-edit-" + c.id}>
            <Edit3 className="w-4 h-4" />
          </button>
        </td>
      )}
    </tr>
  );
}

export function CommissionCard({ c, isAdmin, onEdit }) {
  var pCls = PRODUCT_COLORS_MAP[c.product_type] || "";
  var pLabel = PRODUCT_LABELS[c.product_type] || c.product_type;
  var sCls = STATUS_COLORS_MAP[c.status] || "";
  var sLabel = STATUS_LABELS[c.status] || c.status;
  return (
    <div className="p-4" data-testid={"commission-card-" + c.id}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-sm font-semibold text-[#0A2F3D]">{c.lead_name || "-"}</p>
          <span className={"px-2 py-0.5 text-xs font-medium rounded-full mt-1 inline-block " + pCls}>{pLabel}</span>
        </div>
        <span className={"px-2.5 py-1 text-xs font-medium rounded-full " + sCls}>{sLabel}</span>
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm font-semibold text-emerald-600">{fmtFull(c.value)}</span>
        {isAdmin && (
          <button onClick={function() { onEdit(c); }} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"><Edit3 className="w-4 h-4" /></button>
        )}
      </div>
    </div>
  );
}

export function BrokerRankItem({ b, idx, maxVal, color }) {
  var pct = Math.round((b.total / maxVal) * 100);
  return (
    <div className="flex items-center gap-3" data-testid={"broker-rank-" + idx}>
      <span className="w-6 text-xs font-bold text-gray-400 text-right">{idx + 1}.</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-[#0A2F3D] truncate">{b.broker_name}</span>
          <span className="text-sm font-semibold text-emerald-600 ml-2">{fmtVal(b.total)}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: pct + "%", backgroundColor: color }} />
        </div>
      </div>
    </div>
  );
}

export function EditCommissionModal({ commission, onClose, onSave }) {
  var statusVal = useState(commission.status);
  var valueVal = useState(commission.value);
  var notesVal = useState(commission.notes || "");
  var loadingState = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    loadingState[1](true);
    try {
      await api.put("/commissions/" + commission.id, { status: statusVal[0], value: parseFloat(valueVal[0]), notes: notesVal[0] || null });
      onSave();
    } catch(err) { console.error(err); } finally { loadingState[1](false); }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md" data-testid="edit-commission-modal">
        <div className="bg-white rounded-2xl shadow-2xl">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h2 className="text-lg font-bold text-[#0A2F3D]">Editar Comissão</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <p className="text-xs text-gray-400 mb-1">Lead</p>
              <p className="text-sm font-medium text-[#0A2F3D]">{commission.lead_name || "-"}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Valor (R$)</label>
                <input type="number" value={valueVal[0]} onChange={function(e) { valueVal[1](e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30"
                  step="0.01" data-testid="edit-commission-value" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Status</label>
                <select value={statusVal[0]} onChange={function(e) { statusVal[1](e.target.value); }}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 bg-white"
                  data-testid="edit-commission-status">
                  <option value="pendente">Pendente</option>
                  <option value="processando">Processando</option>
                  <option value="pago">Pago</option>
                  <option value="travada">Travada</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Observação</label>
              <textarea value={notesVal[0]} onChange={function(e) { notesVal[1](e.target.value); }}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/30 resize-none"
                rows="2" data-testid="edit-commission-notes" />
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button type="submit" disabled={loadingState[0]} className="flex-1 px-4 py-2.5 bg-[#3B82F6] text-white rounded-xl text-sm font-medium hover:bg-[#2563EB] disabled:opacity-50" data-testid="edit-commission-submit">
                {loadingState[0] ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
